import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, CircularProgress, Chip, Dialog,
  DialogContent, RadioGroup, FormControlLabel, Radio, TextField,
  InputAdornment, Alert, Divider, Avatar,
} from '@mui/material';
import {
  LockOutlined, CreditCard, AccountBalance, QrCode2,
  CheckCircle, StarOutlined, RocketLaunch, WorkspacePremium,
  WarningAmber, Refresh,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useThemeModeContext } from '../hooks/useThemeMode';
import { food, neu } from '../theme';
import apiService from '../services/api';
import { PricingPlan } from '../types';

// ── Helpers ────────────────────────────────────────────────────────────────────
function daysUntilExpiry(expiry: Date | string | undefined): number | null {
  if (!expiry) return null;
  const exp = new Date(expiry);
  const now = new Date();
  if (isNaN(exp.getTime())) return null;
  const diff = (exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  return diff;
}

function isPlanExpired(expiry: Date | string | undefined): boolean {
  const days = daysUntilExpiry(expiry);
  return days !== null && days <= 0;
}

const PAYMENT_METHODS = [
  { id: 'upi',  label: 'UPI / QR Code',    icon: <QrCode2 /> },
  { id: 'card', label: 'Debit / Credit Card', icon: <CreditCard /> },
  { id: 'bank', label: 'Bank Transfer (NEFT)', icon: <AccountBalance /> },
];

const PLAN_ICONS: Record<string, React.ReactNode> = {
  starter:     <RocketLaunch sx={{ fontSize: 22 }} />,
  professional: <WorkspacePremium sx={{ fontSize: 22 }} />,
  enterprise:  <StarOutlined sx={{ fontSize: 22 }} />,
};

function planIcon(name: string): React.ReactNode {
  const key = Object.keys(PLAN_ICONS).find(k => name.toLowerCase().includes(k));
  return key ? PLAN_ICONS[key] : <StarOutlined sx={{ fontSize: 22 }} />;
}

// ── Plan Expiry Banner (soft warning, shown inside dashboard) ─────────────────
interface BannerProps { daysLeft: number; onUpgrade: () => void; }

export const PlanExpiryBanner: React.FC<BannerProps> = ({ daysLeft, onUpgrade }) => {
  const { isDark } = useThemeModeContext();
  const urgent = daysLeft <= 1;

  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
      <Box sx={{
        px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        background: urgent
          ? (isDark ? 'rgba(239,68,68,0.18)' : 'rgba(239,68,68,0.1)')
          : (isDark ? 'rgba(234,179,8,0.15)' : 'rgba(234,179,8,0.1)'),
        borderBottom: `1px solid ${urgent ? 'rgba(239,68,68,0.3)' : 'rgba(234,179,8,0.3)'}`,
        backdropFilter: 'blur(12px)',
      }}>
        <WarningAmber sx={{ color: urgent ? '#ef4444' : '#eab308', fontSize: 20 }} />
        <Typography sx={{ color: urgent ? '#ef4444' : '#b45309', fontWeight: 600, fontSize: '0.85rem', flex: 1 }}>
          {urgent
            ? `⚡ Your Demo plan expires ${daysLeft <= 0 ? 'today' : 'tomorrow'}! Upgrade now to keep access.`
            : `🕐 Your 7-day Demo plan expires in ${Math.ceil(daysLeft)} day${daysLeft > 1 ? 's' : ''}. Upgrade to continue.`}
        </Typography>
        <Button
          size="small" variant="contained"
          onClick={onUpgrade}
          sx={{
            borderRadius: '9999px', fontWeight: 700, fontSize: '0.78rem', px: 2.5,
            background: urgent
              ? 'linear-gradient(135deg, #ef4444, #dc2626)'
              : 'linear-gradient(135deg, #f59e0b, #d97706)',
            boxShadow: 'none',
            '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' },
          }}
        >
          Upgrade Plan
        </Button>
      </Box>
    </motion.div>
  );
};

// ── Payment Modal ──────────────────────────────────────────────────────────────
interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  plans: PricingPlan[];
  onSuccess: () => void;
  isDark: boolean;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ open, onClose, plans, onSuccess, isDark }) => {
  const { currentTenant } = useAuth();
  const n = isDark ? neu.dark : neu.light;

  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  const [billingCycle, setBillingCycle]  = useState<'monthly' | 'yearly'>('monthly');
  const [payMethod, setPayMethod]        = useState<string>('upi');
  const [cardNum, setCardNum]            = useState('');
  const [cardExp, setCardExp]            = useState('');
  const [cardCvv, setCardCvv]            = useState('');
  const [cardName, setCardName]          = useState('');
  const [paying, setPaying]              = useState(false);
  const [paySuccess, setPaySuccess]      = useState(false);
  const [payError, setPayError]          = useState('');

  const [globalGateways, setGlobalGateways] = useState<any[]>([]);
  const [loadingGateways, setLoadingGateways] = useState(false);

  useEffect(() => {
    if (open) {
      const fetchGateways = async () => {
        setLoadingGateways(true);
        try {
          const activeGws = await apiService.getRestaurantGateways('00000000-0000-0000-0000-000000000000');
          const filtered = activeGws.filter((g: any) => g.isActive);
          setGlobalGateways(filtered);
          if (filtered.length > 0) {
            const first = filtered[0];
            if (first.name === 'Razorpay') setPayMethod('razorpay');
            else if (first.name === 'Paytm') setPayMethod('paytm');
            else if (first.name === 'UPI') setPayMethod('upi_qr');
          } else {
            setPayMethod('upi');
          }
        } catch (error) {
          console.error('Failed to load global gateways:', error);
          setPayMethod('upi');
        } finally {
          setLoadingGateways(false);
        }
      };
      fetchGateways();
    }
  }, [open]);

  const finalPrice = selectedPlan
    ? billingCycle === 'yearly' && selectedPlan.priceYearly
      ? selectedPlan.priceYearly
      : selectedPlan.priceMonthly
    : 0;

  const handlePay = async () => {
    if (!selectedPlan || !currentTenant) { setPayError('Please select a plan'); return; }
    if (payMethod === 'card') {
      if (!cardNum || cardNum.replace(/\s/g,'').length < 16) { setPayError('Enter a valid 16-digit card number'); return; }
      if (!cardExp || !/^\d{2}\/\d{2}$/.test(cardExp)) { setPayError('Enter expiry as MM/YY'); return; }
      if (!cardCvv || cardCvv.length < 3) { setPayError('Enter a valid CVV'); return; }
      if (!cardName.trim()) { setPayError('Enter cardholder name'); return; }
    }
    setPayError('');
    setPaying(true);
    try {
      let methodStr = 'upi';
      if (payMethod === 'card') methodStr = 'card';
      else if (payMethod === 'bank') methodStr = 'bank';
      else if (payMethod === 'razorpay') methodStr = 'razorpay';
      else if (payMethod === 'paytm') methodStr = 'paytm';
      else if (payMethod === 'upi_qr') methodStr = 'upi';

      await apiService.createSubscriptionPayment({
        tenantId: currentTenant.id,
        pricingPlanId: selectedPlan.id,
        amount: finalPrice,
        billingCycle: billingCycle,
        paymentMethod: methodStr,
        transactionId: `TXN-${Math.random().toString(36).substr(2, 9).toUpperCase()}`
      });
      setPaySuccess(true);
      setTimeout(() => { onSuccess(); }, 2200);
    } catch (err: any) {
      setPayError(err?.response?.data?.message || 'Payment processing failed. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)',
      '& fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
      '&:hover fieldset': { borderColor: food.red },
      '&.Mui-focused fieldset': { borderColor: food.red },
    },
    '& .MuiInputLabel-root': { color: n.textMuted },
    '& .MuiInputLabel-root.Mui-focused': { color: food.red },
    '& .MuiOutlinedInput-input': { color: n.textPrimary },
  };

  const paymentMethodsToRender = globalGateways.length > 0
    ? (globalGateways.map(g => {
        if (g.name === 'Razorpay') {
          return { id: 'razorpay', label: `${g.displayName} (Card, UPI, Wallet)`, icon: <CreditCard /> };
        }
        if (g.name === 'Paytm') {
          return { id: 'paytm', label: `${g.displayName} (Wallet, UPI, Netbanking)`, icon: <AccountBalance /> };
        }
        if (g.name === 'UPI') {
          return { id: 'upi_qr', label: `${g.displayName} (${g.apiKey})`, icon: <QrCode2 /> };
        }
        return null;
      }).filter(Boolean) as { id: string; label: string; icon: React.ReactNode }[])
    : PAYMENT_METHODS;

  return (
    <Dialog open={open} onClose={!paying && !paySuccess ? onClose : undefined} maxWidth="md" fullWidth
      PaperProps={{ sx: {
        borderRadius: '28px',
        background: isDark ? 'rgba(20,18,16,0.96)' : 'rgba(255,255,255,0.97)',
        backdropFilter: 'blur(40px)',
        border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
        boxShadow: '0 32px 80px rgba(0,0,0,0.4)',
        overflow: 'hidden',
      }}}>
      <DialogContent sx={{ p: 0 }}>
        {paySuccess ? (
          // ── Payment Success ────────────────────────────────────────────
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
            <Box sx={{ textAlign: 'center', p: 6 }}>
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.1, type: 'spring', stiffness: 250 }}>
                <Box sx={{ width: 90, height: 90, borderRadius: '50%', bgcolor: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3, boxShadow: '0 12px 40px rgba(34,197,94,0.4)' }}>
                  <CheckCircle sx={{ fontSize: 50, color: '#fff' }} />
                </Box>
              </motion.div>
              <Typography variant="h5" sx={{ fontWeight: 800, color: n.textPrimary, fontFamily: 'Space Grotesk', mb: 1 }}>Payment Successful!</Typography>
              <Typography sx={{ color: n.textMuted, fontSize: '0.9rem' }}>
                Your <strong>{selectedPlan?.name}</strong> plan is now active.
                {billingCycle === 'yearly' ? ' Valid for 1 year.' : ' Valid for 1 month.'}
              </Typography>
              <Typography sx={{ color: n.textMuted, fontSize: '0.82rem', mt: 1 }}>Redirecting to dashboard…</Typography>
            </Box>
          </motion.div>
        ) : (
          <Box sx={{ display: 'flex', minHeight: 540 }}>
            {/* ── Left: Plan Selector ──────────────────────────────────── */}
            <Box sx={{
              width: { xs: '100%', sm: '55%' }, p: { xs: 3, sm: 4 }, overflowY: 'auto',
              borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'}`,
            }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: n.textPrimary, fontFamily: 'Space Grotesk', mb: 0.5 }}>
                Choose a Plan
              </Typography>
              <Typography sx={{ color: n.textMuted, fontSize: '0.82rem', mb: 2.5 }}>
                The Demo plan is a one-time offer. Select a paid plan to continue.
              </Typography>

              {/* Billing cycle toggle */}
              <Box sx={{ display: 'flex', gap: 0.75, mb: 3, p: 0.5, borderRadius: '12px', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', width: 'fit-content' }}>
                {(['monthly', 'yearly'] as const).map(cycle => (
                  <Button key={cycle} size="small" onClick={() => setBillingCycle(cycle)}
                    sx={{
                      borderRadius: '10px', fontWeight: 700, fontSize: '0.78rem', px: 2.5, py: 0.8, textTransform: 'none',
                      background: billingCycle === cycle ? (isDark ? 'rgba(255,255,255,0.1)' : '#fff') : 'transparent',
                      color: billingCycle === cycle ? n.textPrimary : n.textMuted,
                      boxShadow: billingCycle === cycle ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
                    }}>
                    {cycle === 'yearly' ? 'Yearly (Save 20%)' : 'Monthly'}
                  </Button>
                ))}
              </Box>

              {/* Plan cards */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {plans.map(plan => {
                  const price = billingCycle === 'yearly' && plan.priceYearly ? plan.priceYearly : plan.priceMonthly;
                  const isSelected = selectedPlan?.id === plan.id;
                  return (
                    <motion.div key={plan.id} whileHover={{ scale: 1.01 }} transition={{ duration: 0.15 }}>
                      <Box
                        onClick={() => setSelectedPlan(plan)}
                        sx={{
                          p: 2.5, borderRadius: '18px', cursor: 'pointer', transition: 'all 0.2s',
                          border: isSelected
                            ? `2px solid ${food.red}`
                            : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                          background: isSelected
                            ? (isDark ? 'rgba(224,42,42,0.12)' : 'rgba(224,42,42,0.05)')
                            : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)'),
                          boxShadow: isSelected ? `0 4px 20px ${food.redGlow}` : 'none',
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 38, height: 38, bgcolor: isSelected ? food.red : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'), color: isSelected ? '#fff' : n.textMuted, borderRadius: '10px' }}>
                              {planIcon(plan.name)}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 700, color: n.textPrimary, fontSize: '0.9rem' }}>{plan.name}</Typography>
                              {plan.description && <Typography sx={{ color: n.textMuted, fontSize: '0.72rem' }}>{plan.description}</Typography>}
                            </Box>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontWeight: 800, color: isSelected ? food.red : n.textPrimary, fontSize: '1.05rem' }}>
                              ₹{price.toLocaleString()}
                            </Typography>
                            <Typography sx={{ color: n.textMuted, fontSize: '0.7rem' }}>/{billingCycle === 'yearly' ? 'yr' : 'mo'}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                          {[
                            plan.hasPOS && 'POS',
                            plan.hasKDS && 'KDS',
                            plan.hasInventory && 'Inventory',
                            plan.hasReports && 'Reports',
                            plan.hasCRM && 'CRM',
                            plan.hasDelivery && 'Delivery',
                            `${plan.maxUsers} Users`,
                            `${plan.maxTables} Tables`,
                          ].filter(Boolean).map(f => (
                            <Chip key={f as string} label={f} size="small" sx={{
                              height: 20, fontSize: '0.65rem', fontWeight: 600,
                              bgcolor: isSelected ? `${food.red}18` : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
                              color: isSelected ? food.red : n.textMuted,
                              border: 'none',
                            }} />
                          ))}
                        </Box>
                      </Box>
                    </motion.div>
                  );
                })}
              </Box>
            </Box>

            {/* ── Right: Payment ────────────────────────────────────────── */}
            <Box sx={{ flex: 1, p: { xs: 3, sm: 4 }, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: n.textPrimary, fontFamily: 'Space Grotesk', mb: 0.5 }}>
                Payment
              </Typography>
              <Typography sx={{ color: n.textMuted, fontSize: '0.82rem', mb: 2.5 }}>
                Secure payment — SSL encrypted
              </Typography>

              {/* Price summary */}
              {selectedPlan && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <Box sx={{ p: 2, borderRadius: '14px', mb: 2.5, bgcolor: isDark ? 'rgba(224,42,42,0.1)' : 'rgba(224,42,42,0.05)', border: `1px solid rgba(224,42,42,0.2)` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography sx={{ color: n.textMuted, fontSize: '0.82rem' }}>{selectedPlan.name} ({billingCycle})</Typography>
                      <Typography sx={{ color: n.textPrimary, fontWeight: 700, fontSize: '0.88rem' }}>₹{finalPrice.toLocaleString()}</Typography>
                    </Box>
                    <Divider sx={{ my: 1, borderColor: 'rgba(224,42,42,0.2)' }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ color: n.textPrimary, fontWeight: 700, fontSize: '0.9rem' }}>Total</Typography>
                      <Typography sx={{ color: food.red, fontWeight: 800, fontSize: '1rem' }}>₹{finalPrice.toLocaleString()}</Typography>
                    </Box>
                  </Box>
                </motion.div>
              )}

              {/* Payment method */}
              {loadingGateways ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
              ) : (
                <RadioGroup value={payMethod} onChange={e => setPayMethod(e.target.value)} sx={{ mb: 2 }}>
                  {paymentMethodsToRender.map(m => (
                    <FormControlLabel
                      key={m.id} value={m.id}
                      control={<Radio size="small" sx={{ color: n.textMuted, '&.Mui-checked': { color: food.red } }} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ color: payMethod === m.id ? food.red : n.textMuted, display: 'flex', alignItems: 'center' }}>{m.icon}</Box>
                          <Typography sx={{ color: n.textPrimary, fontSize: '0.85rem', fontWeight: payMethod === m.id ? 700 : 400 }}>{m.label}</Typography>
                        </Box>
                      }
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </RadioGroup>
              )}

              {/* Details display block */}
              <AnimatePresence mode="wait">
                {payMethod === 'upi_qr' && (() => {
                  const upiGw = globalGateways.find(g => g.name === 'UPI');
                  if (!upiGw) return null;
                  const vpa = upiGw.apiKey || 'earnings@upi';
                  const qrSrc = upiGw.apiSecret && upiGw.apiSecret.startsWith('data:image') ? upiGw.apiSecret : null;
                  return (
                    <motion.div key="upi_qr" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      <Box sx={{ textAlign: 'center', p: 2.5, borderRadius: '18px', bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`, mb: 2.5 }}>
                        <Box sx={{ width: 140, height: 140, mx: 'auto', bgcolor: '#fff', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, p: 1.5, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
                          {qrSrc ? (
                            <img src={qrSrc} alt="UPI QR Code" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          ) : (
                            <QrCode2 sx={{ fontSize: 100, color: '#0f172a' }} />
                          )}
                        </Box>
                        <Typography sx={{ color: n.textMuted, fontSize: '0.78rem' }}>Scan QR Code with any UPI App (GPay, PhonePe, Paytm)</Typography>
                        <Typography sx={{ color: n.textPrimary, fontWeight: 800, fontSize: '0.9rem', mt: 0.75 }}>
                          UPI ID: <span style={{ color: food.red }}>{vpa}</span>
                        </Typography>
                      </Box>
                    </motion.div>
                  );
                })()}

                {payMethod === 'razorpay' && (
                  <motion.div key="razorpay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <Box sx={{ p: 2.5, borderRadius: '18px', bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`, mb: 2.5, textAlign: 'center' }}>
                      <CreditCard sx={{ fontSize: 48, color: '#2D6FEE', mb: 1.5 }} />
                      <Typography sx={{ fontWeight: 700, color: n.textPrimary, fontSize: '0.9rem', mb: 1 }}>Razorpay Checkout</Typography>
                      <Typography sx={{ color: n.textMuted, fontSize: '0.8rem', mb: 2 }}>Secure gateway redirection to complete payment via cards, UPI, or Netbanking.</Typography>
                      <Alert severity="success" icon={<CheckCircle sx={{ fontSize: 18 }} />} sx={{ borderRadius: 2, fontSize: '0.78rem', py: 0.5, textAlign: 'left' }}>
                        Razorpay Sandbox active. Ready to process simulated payments.
                      </Alert>
                    </Box>
                  </motion.div>
                )}

                {payMethod === 'paytm' && (
                  <motion.div key="paytm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <Box sx={{ p: 2.5, borderRadius: '18px', bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`, mb: 2.5, textAlign: 'center' }}>
                      <AccountBalance sx={{ fontSize: 48, color: '#002E7E', mb: 1.5 }} />
                      <Typography sx={{ fontWeight: 700, color: n.textPrimary, fontSize: '0.9rem', mb: 1 }}>Paytm Secure Checkout</Typography>
                      <Typography sx={{ color: n.textMuted, fontSize: '0.8rem', mb: 2 }}>Redirection to Paytm gateway for UPI, wallet, cards and netbanking options.</Typography>
                      <Alert severity="success" icon={<CheckCircle sx={{ fontSize: 18 }} />} sx={{ borderRadius: 2, fontSize: '0.78rem', py: 0.5, textAlign: 'left' }}>
                        Paytm Integration Sandbox active.
                      </Alert>
                    </Box>
                  </motion.div>
                )}

                {payMethod === 'upi' && (
                  <motion.div key="upi" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: '14px', bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`, mb: 2 }}>
                      <Box sx={{ width: 110, height: 110, mx: 'auto', bgcolor: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
                        <QrCode2 sx={{ fontSize: 80, color: '#1a1209' }} />
                      </Box>
                      <Typography sx={{ color: n.textMuted, fontSize: '0.78rem' }}>Scan with any UPI app</Typography>
                      <Typography sx={{ color: n.textPrimary, fontWeight: 700, fontSize: '0.85rem', mt: 0.5 }}>menumaster@upi</Typography>
                    </Box>
                  </motion.div>
                )}

                {payMethod === 'card' && (
                  <motion.div key="card" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                      <TextField label="Card Number" value={cardNum}
                        onChange={e => setCardNum(e.target.value.replace(/\D/g,'').slice(0,16).replace(/(.{4})/g,'$1 ').trim())}
                        fullWidth size="small" placeholder="1234 5678 9012 3456"
                        InputProps={{ startAdornment: <InputAdornment position="start"><CreditCard sx={{ fontSize: 18, color: n.textMuted }} /></InputAdornment> }}
                        sx={inputSx} />
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField label="MM/YY" value={cardExp} onChange={e => setCardExp(e.target.value.replace(/[^0-9/]/g,'').slice(0,5))} fullWidth size="small" placeholder="MM/YY" sx={inputSx} />
                        <TextField label="CVV" value={cardCvv} onChange={e => setCardCvv(e.target.value.replace(/\D/g,'').slice(0,4))} fullWidth size="small" type="password" placeholder="•••" sx={inputSx} />
                      </Box>
                      <TextField label="Cardholder Name" value={cardName} onChange={e => setCardName(e.target.value)} fullWidth size="small" sx={inputSx} />
                    </Box>
                  </motion.div>
                )}

                {payMethod === 'bank' && (
                  <motion.div key="bank" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <Box sx={{ p: 2, borderRadius: '14px', bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`, mb: 2 }}>
                      {[
                        ['Bank Name', 'HDFC Bank'],
                        ['Account No.', '50100123456789'],
                        ['IFSC', 'HDFC0001234'],
                        ['Account Name', 'MenuMaster Technologies Pvt Ltd'],
                      ].map(([label, value]) => (
                        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ color: n.textMuted, fontSize: '0.78rem' }}>{label}</Typography>
                          <Typography sx={{ color: n.textPrimary, fontWeight: 700, fontSize: '0.78rem' }}>{value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>

              {payError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontSize: '0.82rem' }}>{payError}</Alert>}

              <Box sx={{ mt: 'auto' }}>
                <Button
                  variant="contained" fullWidth
                  onClick={handlePay}
                  disabled={paying || !selectedPlan}
                  sx={{
                    py: 1.6, borderRadius: '9999px', fontWeight: 700, fontSize: '0.95rem',
                    background: `linear-gradient(135deg, ${food.red} 0%, ${food.redLight} 100%)`,
                    boxShadow: `0 6px 24px ${food.redGlow}`,
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 10px 32px ${food.redGlow}` },
                    '&.Mui-disabled': { opacity: 0.5 },
                  }}>
                  {paying
                    ? <CircularProgress size={22} sx={{ color: '#fff' }} />
                    : `Pay ₹${finalPrice.toLocaleString()} →`}
                </Button>
                <Typography sx={{ textAlign: 'center', color: n.textMuted, fontSize: '0.7rem', mt: 1.5 }}>
                  🔒 256-bit SSL encrypted · Secured payment
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ── Main PlanExpiryGate ────────────────────────────────────────────────────────
interface GateProps { children: React.ReactNode; }

const PlanExpiryGate: React.FC<GateProps> = ({ children }) => {
  const { user, currentTenant } = useAuth();
  const { isDark } = useThemeModeContext();
  const n = isDark ? neu.dark : neu.light;

  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [planRefreshed, setPlanRefreshed] = useState(false);

  // Only gate for Admin (not SuperAdmin, not Staff)
  const isAdmin = user?.role === 'Admin';

  const planExpiry = currentTenant?.planExpiry;
  const expired    = isAdmin && isPlanExpired(planExpiry);
  const daysLeft   = daysUntilExpiry(planExpiry);
  const showBanner = isAdmin && !expired && daysLeft !== null && daysLeft <= 3 && daysLeft > 0;

  // Load paid plans (excluding "Demo" — one-time only)
  useEffect(() => {
    if ((expired || showBanner) && plans.length === 0) {
      setLoadingPlans(true);
      apiService.getAllPricingPlans()
        .then(all => {
          const paid = all.filter(
            p => p.isActive && !p.name.toLowerCase().includes('demo')
          );
          setPlans(paid);
        })
        .catch(() => {})
        .finally(() => setLoadingPlans(false));
    }
  }, [expired, showBanner, plans.length]);

  const handlePaymentSuccess = () => {
    setShowPayment(false);
    setPlanRefreshed(true);
    // Reload page to refresh tenant data
    setTimeout(() => window.location.reload(), 500);
  };

  // ── EXPIRED: Full screen paywall ──────────────────────────────────────────
  if (expired && !planRefreshed) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: isDark ? food.darkBg : food.bg,
        backgroundImage: isDark
          ? 'radial-gradient(ellipse 70% 55% at 0% 0%, rgba(224,42,42,0.08) 0%, transparent 70%)'
          : `radial-gradient(ellipse 70% 55% at 0% 0%, ${food.bgPeach} 0%, transparent 70%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3,
      }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Box sx={{ maxWidth: 520, width: '100%', textAlign: 'center' }}>

            {/* Lock icon */}
            <Box sx={{
              width: 90, height: 90, borderRadius: '26px', mx: 'auto', mb: 3,
              background: 'linear-gradient(135deg, rgba(224,42,42,0.9), rgba(180,24,24,1))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 12px 40px ${food.redGlow}`,
            }}>
              <LockOutlined sx={{ fontSize: 46, color: '#fff' }} />
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: 'Space Grotesk', color: n.textPrimary, mb: 1, letterSpacing: '-0.03em' }}>
              Your Trial Has Ended
            </Typography>
            <Typography sx={{ color: n.textMuted, fontSize: '0.95rem', mb: 1.5, lineHeight: 1.65 }}>
              Your 7-day Demo plan has expired. Choose a paid plan to continue using MenuMaster.
            </Typography>
            <Chip
              label="Demo plan cannot be reactivated"
              size="small"
              sx={{ mb: 3.5, bgcolor: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)', fontWeight: 600, fontSize: '0.75rem' }}
            />

            {loadingPlans ? (
              <CircularProgress sx={{ color: food.red, mb: 3 }} />
            ) : (
              <Button
                variant="contained" size="large" fullWidth
                onClick={() => setShowPayment(true)}
                startIcon={<CreditCard />}
                sx={{
                  py: 1.8, borderRadius: '9999px', fontWeight: 700, fontSize: '1rem', mb: 2,
                  background: `linear-gradient(135deg, ${food.red} 0%, ${food.redLight} 100%)`,
                  boxShadow: `0 8px 28px ${food.redGlow}`,
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 12px 36px ${food.redGlow}` },
                }}
              >
                Choose a Plan & Upgrade
              </Button>
            )}

            <Button
              startIcon={<Refresh />}
              onClick={() => window.location.reload()}
              sx={{ color: n.textMuted, fontSize: '0.82rem', textTransform: 'none', '&:hover': { color: n.textPrimary } }}
            >
              Refresh page
            </Button>
          </Box>
        </motion.div>

        {/* Payment modal */}
        <PaymentModal
          open={showPayment}
          onClose={() => setShowPayment(false)}
          plans={plans}
          onSuccess={handlePaymentSuccess}
          isDark={isDark}
        />
      </Box>
    );
  }

  // ── ACTIVE (possibly with warning banner) ─────────────────────────────────
  return (
    <>
      {showBanner && (
        <PlanExpiryBanner
          daysLeft={daysLeft!}
          onUpgrade={() => setShowPayment(true)}
        />
      )}
      {children}
      <PaymentModal
        open={showPayment}
        onClose={() => setShowPayment(false)}
        plans={plans}
        onSuccess={handlePaymentSuccess}
        isDark={isDark}
      />
    </>
  );
};

export default PlanExpiryGate;
